import request from '@/api/http'

/**
 * 分页查询结算信息
 * @param {Object} params 查询参数
 * @param {String} [params.settlement_no] - 结算单号(可选，支持模糊查询)
 * @param {String} [params.settlement_title] - 结算标题(可选，支持模糊查询)
 * @param {String} [params.status] - 结算状态(可选)
 * @param {Number} [params.order_id] - 订单ID(可选)
 * @param {String} [params.startDate] - 创建开始时间，格式：YYYY-MM-DD(可选)
 * @param {String} [params.endDate] - 创建结束时间，格式：YYYY-MM-DD(可选)
 * @param {Number} [params.current] - 当前页码(可选，默认为1)
 * @param {Number} [params.pageSize] - 每页记录数(可选，默认为10)
 * @returns {Promise} 返回结算信息列表和分页信息
 */
export function query_settlements_pagination(params) {
  return request({
    url: '/settlements',
    method: 'get',
    params
  })
}

/**
 * 查询指定单位的已确认结算单列表
 * @param {number} unitId 需求方单位ID
 * @returns {Promise} 返回已确认结算单列表
 */
export function query_confirmed_settlements_by_unit(unitId) {
  return request({
    url: '/settlements/confirmed',
    method: 'get',
    params: {
      unitId
    }
  })
}

/**
 * 销售结算分页查询（供应商为当前登录单位的结算单）
 * @param {Object} params 查询参数
 * @param {String} [params.settlementNumber] - 结算编号(可选，模糊查询)
 * @param {String} [params.settlementTitle] - 结算标题(可选，模糊查询)
 * @param {String} [params.startDate] - 开始日期，格式：yyyy-MM-dd(可选)
 * @param {String} [params.endDate] - 结束日期，格式：yyyy-MM-dd(可选)
 * @param {Number} [params.creatorId] - 创建人ID(可选)
 * @param {String} [params.status] - 结算状态(可选)
 * @param {Number} [params.page] - 页码，默认为0
 * @param {Number} [params.size] - 每页数量，默认为10
 * @param {String} [params.sortBy] - 排序字段，默认为id
 * @param {String} [params.sortDir] - 排序方向(asc/desc)，默认为desc
 * @returns {Promise} 返回结算信息列表和分页信息
 */
export function search_sales_settlements_pagination(params) {
  return request({
    url: '/settlements/search/sales',
    method: 'get',
    params
  })
}

/**
 * 采购结算分页查询（需求方是当前登录单位的结算单）
 * @param {Object} params 查询参数
 * @param {String} [params.settlementNumber] - 结算编号(可选，模糊查询)
 * @param {String} [params.settlementTitle] - 结算标题(可选，模糊查询)
 * @param {String} [params.startDate] - 开始日期，格式：yyyy-MM-dd(可选)
 * @param {String} [params.endDate] - 结束日期，格式：yyyy-MM-dd(可选)
 * @param {Number} [params.creatorId] - 创建人ID(可选)
 * @param {String} [params.status] - 结算状态(可选)
 * @param {Number} [params.page] - 页码，默认为0
 * @param {Number} [params.size] - 每页数量，默认为10
 * @param {String} [params.sortBy] - 排序字段，默认为id
 * @param {String} [params.sortDir] - 排序方向(asc/desc)，默认为desc
 * @returns {Promise} 返回结算信息列表和分页信息
 */
export function search_purchase_settlements_pagination(params) {
  return request({
    url: '/settlements/search/purchase',
    method: 'get',
    params
  })
}

/**
 * 分页条件查询结算（已废弃，请使用 search_sales_settlements_pagination 或 search_purchase_settlements_pagination）
 * @deprecated 原接口已拆分为 /settlements/search/sales 和 /settlements/search/purchase
 */
export function search_settlements_pagination(params) {
  return request({
    url: '/settlements/search/sales',
    method: 'get',
    params
  })
}

/**
 * 根据ID查询结算详情
 * @param {number} id - 结算表ID
 * @returns {Promise} - 返回结算详情的Promise对象，包括结算基本信息、关联订单信息和发票信息
 */
export function query_settlement_by_id(id) {
  return request({
    url: `/settlements/${id}`,
    method: 'get'
  })
}

/**
 * 保存结算信息（新增或更新）
 * @param {Object} data - 结算数据
 * @param {Number} [data.id] - 结算表ID(更新时提供，新增时不提供)
 * @param {String} [data.settlement_no] - 结算单号(新增时自动生成)
 * @param {String} data.settlement_title - 结算标题，必填
 * @param {Number} data.create_id - 发起结算的单位ID，必填
 * @param {Number} data.settlement_amount - 总结算金额，必填
 * @param {Array} data.orders - 关联订单列表，必填
 * @param {Number} data.orders[].order_id - 订单ID
 * @param {Number} data.orders[].target_id - 结算对象ID（订单的supplier_id）
 * @param {Number} data.orders[].settlement_amount - 该订单的结算金额
 * @param {Array} data.products - 产品明细列表，必填
 * @param {Number} data.products[].order_id - 订单ID
 * @param {Number} data.products[].order_product_id - 订单产品ID
 * @param {String} data.products[].product_name - 产品名称
 * @param {Number} data.products[].original_quantity - 原始数量
 * @param {Number} data.products[].settlement_quantity - 结算数量
 * @param {String} data.products[].product_unit - 产品单位
 * @param {Number} data.products[].product_price - 产品单价
 * @param {Number} data.products[].price_total - 价格小计
 * @param {Number} data.products[].product_tax_rate - 税率
 * @param {Number} data.products[].product_tax_price - 含税价格
 * @param {String} [data.products[].remark] - 备注
 * @returns {Promise} - 返回保存结果的Promise对象
 */
export function create_settlement(data) {
  return request({
    url: '/settlements',
    method: 'post',
    data
  })
}

/**
 * 删除结算表（逻辑删除）
 * @param {Number} id - 结算表ID
 * @returns {Promise} - 返回删除结果的Promise对象
 */
export function delete_settlement(id) {
return request({
    url: `/settlements/${id}`,
    method: 'delete'
  })
}

/**
 * 获取可结算订单列表
 * @returns {Promise} - 返回可结算订单列表的Promise对象
 */
export function get_settleable_orders() {
  return request({
    url: '/orders/settleable',
    method: 'get'
  })
}

/**
 * 获取所有结算单列表（用于过滤已结算订单）
 * @returns {Promise} - 返回结算单列表的Promise对象
 */
export function get_all_settlements() {
  return request({
    url: '/settlements',
    method: 'get'
  })
}

/**
 * 获取结算单下拉列表（用于资金管理关联结算单）
 * @param {Object} params 查询参数
 * @param {String} [params.fund_type] - 资金类型(应收/应付)
 * @param {Number} [params.create_id] - 创建单位ID
 * @param {String} [params.status] - 结算状态
 * @returns {Promise} - 返回结算单下拉列表的Promise对象
 */
export function get_settlements_dropdown(params = {}) {
  return request({
    url: '/settlement-items/settlements/dropdown',
    method: 'get',
    params
  })
}

/**
 * 创建随机测试结算表
 * @returns {Promise} - 返回创建结果的Promise对象
 */
export async function create_random_test_settlement() {
  try {
// 先尝试获取一个实际存在的订单ID
    let order_id;
    try {
const ordersResponse = await request({
        url: '/orders',
        method: 'get',
        params: { pageSize: 5 }
      })
      
      let ordersList = [];
      // 尝试从不同的数据结构中提取订单列表
      if (ordersResponse.data && ordersResponse.data.data && Array.isArray(ordersResponse.data.data.data)) {
        ordersList = ordersResponse.data.data.data;
      } else if (ordersResponse.data && ordersResponse.data.data && Array.isArray(ordersResponse.data.data)) {
        ordersList = ordersResponse.data.data;
      } else if (ordersResponse.data && Array.isArray(ordersResponse.data.items)) {
        ordersList = ordersResponse.data.items;
      } else if (Array.isArray(ordersResponse.data)) {
        ordersList = ordersResponse.data;
      }
      
      if (ordersList.length > 0) {
        // 从返回的订单中随机选择一个
        const randomOrder = ordersList[Math.floor(Math.random() * ordersList.length)];
        order_id = randomOrder.id;
} else {
        // 如果没有找到订单，使用默认值
        order_id = Math.floor(Math.random() * 5) + 1;
}
    } catch (error) {
      console.error('查询订单失败:', error);
      // 如果查询订单失败，使用默认值
      order_id = Math.floor(Math.random() * 5) + 1;
}
    
    // 随机生成结算数据
    const currentDate = new Date()
    const periodStart = new Date(currentDate)
    periodStart.setDate(1) // 当月第一天
    
    const periodEnd = new Date(currentDate)
    periodEnd.setDate(15) // 当月15号
    
    const randomData = {
      settlement_title: `测试结算_${new Date().getTime()}`,
      period_start: periodStart.toISOString().split('T')[0],
      period_end: periodEnd.toISOString().split('T')[0],
      order_id: order_id, // 使用查询到的或默认订单ID
      settlement_amount: (Math.floor(Math.random() * 100000) / 100 + 5000).toFixed(2), // 随机金额 5000-6000
      status: "待结算"
    }
    
// 调用创建结算表API
    const response = await create_settlement(randomData)
return response
  } catch (error) {
    console.error('创建随机测试结算表失败:', error)
    return Promise.reject({
      success: false,
      message: '创建随机测试结算表失败: ' + (error.message || '未知错误'),
      error: error.toString()
    })
  }
}

// 撤销结算单
export function cancel_settlement(id) {
  return request({
    url: `/settlements/${id}/cancel`,
    method: 'put'
  })
}

// 开具发票
export function invoice_settlement(id, data) {
  return request({
    url: `/settlements/${id}/invoice`,
    method: 'post',
    data
  })
}

/**
 * 查询已签收的发货记录产品信息（用于结算页面产品选择）
 * @param {Object} params 查询参数
 * @param {String} [params.productName] - 产品名称（模糊查询）
 * @param {String} [params.requesterName] - 需求方名称（模糊查询）
 * @param {String} [params.secondaryUnitName] - 二级单位名称（模糊查询）
 * @param {String} [params.orderNumber] - 订单编号（模糊查询）
 * @param {String} [params.settlementStatus] - 结算状态过滤（UNSETTLED 未结算 / SETTLED 已结算 / ALL 全部）
 * @param {String} [params.pricingMethod] - 定价方式过滤（FIXED/FLOATING 或 固定价/变动价）
 * @param {Number} [params.page] - 页码（从0开始）
 * @param {Number} [params.size] - 每页大小
 * @param {String} [params.sort] - 排序字段和方向，默认：shipmentDate,desc
 * @returns {Promise} 返回已签收的发货记录产品信息列表和分页信息
 */
export function query_settlement_products(params) {
  return request({
    url: '/shipment-list/for-settlement',
    method: 'get',
    params
  }).then(response => {
    // 返回axios响应的data部分，即后端返回的实际数据
    return response.data
  })
}

/**
 * 需求方确认结算单
 * @param {Number} id - 结算ID
 * @param {Object} data - 确认数据
 * @param {Boolean} data.needAudit - 是否需要审核
 * @param {Number} [data.auditorId] - 审核人ID（当needAudit为true时必填）
 * @returns {Promise} 返回确认结果
 */
export function confirm_settlement(id, data) {
  return request({
    url: `/settlements/${id}/confirm`,
    method: 'put',
    data
  })
}

/**
 * 设置审核人
 * @param {Number} id - 结算ID
 * @param {Object} data - 审核人数据
 * @param {Boolean} data.needAudit - 是否需要审核
 * @param {Number} [data.auditorId] - 审核人ID（当needAudit为true时必填）
 * @returns {Promise} 返回设置结果
 */
export function set_settlement_auditor(id, data) {
  return request({
    url: `/settlements/${id}/set-auditor`,
    method: 'put',
    data
  })
}

/**
 * 审核结算单
 * @param {Number} id - 结算ID
 * @param {Object} data - 审核数据
 * @param {Boolean} data.approved - 是否通过审核
 * @param {String} [data.auditRemark] - 审核备注
 * @returns {Promise} 返回审核结果
 */
export function audit_settlement(id, data) {
  return request({
    url: `/settlements/${id}/audit`,
    method: 'put',
    data
  })
}

/**
 * 回退结算单状态
 * @param {Number} id - 结算ID
 * @returns {Promise} 返回回退结果
 */
export function rollback_settlement(id) {
  return request({
    url: `/settlements/${id}/rollback`,
    method: 'put'
  })
}

/**
 * 完成结算
 * @param {Number} id - 结算ID
 * @returns {Promise} 返回完成结果
 */
export function finish_settlement(id) {
  return request({
    url: `/settlements/${id}/finish`,
    method: 'put'
  })
}

/**
 * 转发结算单
 * @param {Number} id - 原结算ID
 * @param {Object} data - 转发数据
 * @param {String} data.settlementNumber - 新结算编号
 * @param {String} data.settlementTitle - 新结算标题
 * @param {Number} data.amount - 新结算金额
 * @param {Array} data.demandUnitIds - 新需求方单位ID列表
 * @param {Object} data.supplier - 供应商信息
 * @param {Array} data.logisticsInfoIds - 物流信息ID列表
 * @param {Number} data.forwardingMarkupRate - 价格上浮比率
 * @param {Array} data.settlementItems - 结算项目列表
 * @returns {Promise} 返回转发结果
 */
export function forward_settlement(id, data) {
  return request({
    url: `/settlements/${id}/forward`,
    method: 'post',
    data
  })
}

/**
 * 设置供应商审核人
 * @param {Number} id - 结算ID
 * @param {Object} data - 供应商审核人数据
 * @param {Boolean} data.needSupplierAudit - 是否需要供应商审核
 * @param {Number} [data.supplierAuditorId] - 供应商审核人ID（当needSupplierAudit为true时必填）
 * @returns {Promise} 返回设置结果
 */
export function set_supplier_auditor(id, data) {
  return request({
    url: `/settlements/${id}/set-supplier-auditor`,
    method: 'put',
    data
  })
}

/**
 * 供应商审核结算单
 * @param {Number} id - 结算ID
 * @param {Object} data - 供应商审核数据
 * @param {Boolean} data.approved - 是否通过审核
 * @param {String} [data.auditRemark] - 审核备注
 * @returns {Promise} 返回审核结果
 */
export function supplier_audit_settlement(id, data) {
  return request({
    url: `/settlements/${id}/supplier-audit`,
    method: 'put',
    data
  })
}

/**
 * 最终确认结算单
 * @param {Number} id - 结算ID
 * @returns {Promise} 返回确认结果
 */
export function final_confirm_settlement(id) {
  return request({
    url: `/settlements/${id}/final-confirm`,
    method: 'put'
  })
}

export default {
  query_settlements_pagination,
  query_confirmed_settlements_by_unit,
  search_sales_settlements_pagination,
  search_purchase_settlements_pagination,
  search_settlements_pagination,
  query_settlement_by_id,
  create_settlement,
  delete_settlement,
  get_settleable_orders,
  get_all_settlements,
  create_random_test_settlement,
  cancel_settlement,
  invoice_settlement,
  query_settlement_products,
  confirm_settlement,
  set_settlement_auditor,
  audit_settlement,
  rollback_settlement,
  finish_settlement,
  forward_settlement,
  set_supplier_auditor,
  supplier_audit_settlement,
  final_confirm_settlement
}